import { useState, useRef, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../store';
import {uploadFileWithFolderPath, pauseUpload as pauseTask, resumeUpload as resumeTask, cancelUpload as cancelTask} from '../store/slices/uploadSlice';
interface UploadProgress {
  processedFiles: number;
  totalFiles: number;
  progress: number;
  successfulUploads: number;
  failedUploads: number;
  activeUploads: number;
  remainingFiles: number;
}

interface UploadState {
  isUploading: boolean;
  isPaused: boolean;
  progress: UploadProgress | null;
  currentBatch: number;
  batchSize: number;
}

interface UseChunkedUploadOptions {
  onProgress?: (progress: UploadProgress) => void;
  onComplete?: (result: { totalFiles: number; successfulUploads: number; failedUploads: number }) => void;
  onError?: (error: string) => void;
  onFileComplete?: (fileId: string, success: boolean, error?: string) => void;
  onFileProgress?: (fileId: string, progress: number, details?: { loaded: number; total: number }) => void;
  onBatchStart?: (batchNumber: number, batchSize: number, remainingFiles: number) => void;
}

export const useChunkedUpload = (options: UseChunkedUploadOptions = {}) => {
  const dispatch = useDispatch<AppDispatch>();
  const rows = useSelector((state: RootState) => state.upload.uploads);
  const callbacks = useRef(options); callbacks.current = options;
  const ids = useRef<string[]>([]);
  const [state, setState] = useState<UploadState>({isUploading: false, isPaused: false, progress: null, currentBatch: 0, batchSize: 3});
  useEffect(() => {
    const active = rows.filter(row => ids.current.includes(row.id) && row.status !== 'cancelled');
    if (!active.length) return;
    const completed = active.filter(row => row.status === 'completed').length;
    const failed = active.filter(row => row.status === 'error').length;
    const bytes = active.reduce((sum, row) => sum + row.size, 0);
    const progress = {processedFiles: completed + failed, totalFiles: active.length,
      progress: bytes ? active.reduce((sum, row) => sum + row.size * row.progress, 0) / bytes : 0,
      successfulUploads: completed, failedUploads: failed, activeUploads: active.filter(row => ['uploading','processing'].includes(row.status)).length,
      remainingFiles: active.length - completed - failed};
    setState(previous => ({...previous, isUploading: progress.remainingFiles > 0, isPaused: active.some(row => row.status === 'paused'), progress}));
    callbacks.current.onProgress?.(progress);
  }, [rows]);
  const startUpload = useCallback(async (files: File[], folderId?: string, folderMap?: Map<string,string>, fileFolderMap?: (string | undefined)[], clientFileIds?: string[]) => {
    const batchId = crypto.randomUUID();
    const taskIds = files.map((_, index) => clientFileIds?.[index] || crypto.randomUUID()); ids.current = [...ids.current, ...taskIds];
    const result = await Promise.allSettled(files.map(async (file, index) => {
      const id = taskIds[index];
      try {
        const result = await dispatch(uploadFileWithFolderPath({file, batchId, uploadId: id, folderId: fileFolderMap?.[index] || folderId,
          webkitRelativePath: file.webkitRelativePath, onProgress: progress => callbacks.current.onFileProgress?.(id, progress, {loaded: file.size * progress / 100, total: file.size})})).unwrap();
        if (!result.cancelled) callbacks.current.onFileComplete?.(id, true);
        return result;
      } catch (error: any) {callbacks.current.onFileComplete?.(id, false, error.message); throw error;}
    }));
    callbacks.current.onComplete?.({totalFiles: files.length, successfulUploads: result.filter(row => row.status === 'fulfilled' && !(row.value as any).cancelled).length, failedUploads: result.filter(row => row.status === 'rejected').length});
  }, [dispatch]);
  const pauseUpload = useCallback(() => ids.current.forEach(id => dispatch(pauseTask(id))), [dispatch]);
  const resumeUpload = useCallback(() => ids.current.forEach(id => dispatch(resumeTask(id))), [dispatch]);
  const cancelUpload = useCallback(() => {ids.current.forEach(id => dispatch(cancelTask(id)));setState(previous => ({...previous, isUploading:false,isPaused:false}));}, [dispatch]);
  return {state, startUpload, pauseUpload, resumeUpload, cancelUpload};
};
